<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import EntrChrome from '@/ui/components/EntrChrome.vue';
import { usePrintingController } from './controller';
import type { PrintingScreenEvents, PrintingScreenProps } from './types';

defineProps<PrintingScreenProps>();
const emit = defineEmits<PrintingScreenEvents>();
const controller = usePrintingController(emit);
const { t } = useI18n();
</script>

<template>
  <section class="screen print-screen" data-testid="screen-printing">
    <h1 class="t-display headline reveal" style="--reveal-order: 0">{{ t('printing.title') }}</h1>

    <div class="printer reveal" style="--reveal-order: 1" aria-hidden="true">
      <div class="body" />
      <div class="slot" />
      <div class="photo-window">
        <div class="photo" />
      </div>
    </div>

    <div class="steps reveal" style="--reveal-order: 2">
      <p><span>1.</span>{{ t('printing.step1') }}</p>
      <p><span>2.</span>{{ t('printing.step2') }}</p>
      <p><span>3.</span>{{ t('printing.step3') }}</p>
    </div>

    <p class="eta reveal" style="--reveal-order: 3">{{ t('printing.eta') }}</p>

    <EntrChrome :step="4" @help="controller.help" />
  </section>
</template>

<style scoped>
.headline {
  position: absolute;
  top: 240px;
  left: 96px;
  right: 96px;
  font-size: 64px;
  white-space: nowrap;
  text-align: center;
}

.printer {
  position: absolute;
  top: 560px;
  left: 50%;
  width: 360px;
  height: 360px;
  transform: translateX(-50%);
}

.body {
  position: absolute;
  left: 20px;
  bottom: 0;
  width: 320px;
  height: 240px;
  border: 4px solid var(--entr-text);
  border-radius: 20px;
  background: var(--entr-ink);
}

.slot {
  position: absolute;
  left: 60px;
  bottom: 200px;
  width: 240px;
  height: 6px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.3);
}

.photo-window {
  position: absolute;
  left: 90px;
  bottom: 60px;
  width: 180px;
  height: 240px;
  overflow: visible;
}

.photo {
  width: 180px;
  height: 120px;
  border: 3px solid var(--entr-text);
  border-radius: 4px;
  background: linear-gradient(
    135deg,
    var(--entr-branding-blue) 0%,
    #6c8dc8 52%,
    var(--entr-canvas-blue) 100%
  );
  animation: emerge 3000ms ease-out infinite;
}

.steps {
  position: absolute;
  top: 1060px;
  left: 0;
  right: 0;
  display: grid;
  gap: 16px;
  justify-items: center;
}

.steps p {
  color: var(--entr-text);
  font-size: 40px;
  line-height: 1.3;
}

.steps span {
  margin-right: 12px;
  color: var(--entr-text-soft);
}

.eta {
  position: absolute;
  top: 1500px;
  left: 0;
  right: 0;
  color: var(--entr-text-soft);
  font-size: 40px;
  text-align: center;
}

@keyframes emerge {
  0% {
    opacity: 0;
    transform: translateY(-100%);
  }

  20%,
  80% {
    opacity: 1;
  }

  100% {
    opacity: 0;
    transform: translateY(80%);
  }
}
</style>
